import { Amplify, ResourcesConfig } from "aws-amplify";
import { Amplify as AmplifyCore } from "@aws-amplify/core";
import { fetchAuthSession } from "aws-amplify/auth";
import { logger } from "@amzn/afxtaco-utilities";
import { config } from "../../config";

// Global flag to ensure Amplify is only configured once
// This prevents React Strict Mode from causing double configuration
let isAmplifyConfigured = false;

/**
 * Initializes and configures AWS Amplify with custom credentials provider
 * for Cognito Federated Identities and API Gateway, including OAuth configuration.
 *
 * This should be called during app initialization after the config is loaded.
 * It's safe to call multiple times - it will only configure Amplify once.
 *
 * @example
 * ```typescript
 * await config.initialize();
 * await configureAmplify();
 * ```
 */
export async function configureAmplify(): Promise<void> {
  // Prevent double configuration
  if (isAmplifyConfigured) {
    logger.info({
      loggerMsg: "Amplify already configured, skipping",
    });
    return;
  }
  const authConfig = config.getAuthConfig();
  const apiGatewayConfig = config.getApiGatewayConfig();

  // Try to get auth token to verify if session exists, but don't fail if it doesn't
  // During OAuth callback, there won't be a session yet
  try {
    await fetchAuthSession();
  } catch (error) {
    // Expected during OAuth callback or when user is not authenticated yet
    logger.info({
      loggerMsg:
        "No existing session during Amplify configuration (expected during OAuth callback)",
      error,
    });
  }

  const amplifyConfig: ResourcesConfig = {
    Auth: {
      Cognito: {
        identityPoolId: authConfig.identityPoolId,
        userPoolId: authConfig.userPoolId,
        userPoolClientId: authConfig.userPoolWebClientId,
        loginWith: {
          oauth: {
            domain: authConfig.domain,
            scopes: authConfig.scope,
            redirectSignIn: [authConfig.redirectUrl],
            redirectSignOut: [authConfig.redirectUrl],
            responseType: authConfig.responseType as "code" | "token",
          },
        },
      },
    },
    API: {
      REST: {
        [apiGatewayConfig.name]: {
          endpoint: apiGatewayConfig.url,
          region: authConfig.region,
        },
      },
    },
  };

  // Configure both Amplify instances to ensure consistency
  Amplify.configure(amplifyConfig);
  AmplifyCore.configure(amplifyConfig);

  // Mark as configured
  isAmplifyConfigured = true;
}
